import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
    Button, TextField, MenuItem, Table, TableHead, TableRow, TableCell,
    TableBody, Paper, Typography, Box, Dialog, DialogTitle,
    DialogContent, DialogActions, IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { formatDateTime } from '../../lib/dateFormat';

const PhoneNumbers = () => {
    const [numbers, setNumbers] = useState([]);
    const [agents, setAgents] = useState([]);
    const [form, setForm] = useState({ number: '', inboundAgentId: '', outboundAgentId: '' });

    const [twilioModalOpen, setTwilioModalOpen] = useState(false);
    const [twilioForm, setTwilioForm] = useState({
        phoneNumber: '',
        accountSid: '',
        authToken: '',
        label: '',
        inboundAgentId: '',
        outboundAgentId: ''
    });

    const [providerModalOpen, setProviderModalOpen] = useState(null); // 'vonage' | 'telnyx' | 'sip'
    const [providerForm, setProviderForm] = useState({
        phoneNumber: '',
        label: '',
        credentials: {},
        inboundAgentId: '',
        outboundAgentId: ''
    });

    const [editingId, setEditingId] = useState(null);
    const [formError, setFormError] = useState('');
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [selectedDeleteId, setSelectedDeleteId] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const numsRes = await axios.get('/api/vapi/phone-numbers');
            const agentsRes = await axios.get('/api/vapi/assistants');
            setNumbers(numsRes.data);
            setAgents(agentsRes.data);
        } catch (err) {
            console.error('Error fetching data:', err);
        }
    };

    const validatePhoneNumber = (number) => /^\+\d{10,15}$/.test(number);

    const handleAddOrUpdate = async () => {
        if (!validatePhoneNumber(form.number)) {
            setFormError('Phone number must be in E.164 format, e.g., +1234567890');
            return;
        }

        try {
            if (editingId) {
                await axios.put(`/api/vapi/phone-numbers/${editingId}`, {
                    number: form.number,
                    inbound_agent_id: form.inboundAgentId || null,
                    outbound_agent_id: form.outboundAgentId || null,
                });
            } else {
                await axios.post('/api/vapi/phone-numbers', {
                    number: form.number,
                    inbound_agent_id: form.inboundAgentId || null,
                    outbound_agent_id: form.outboundAgentId || null,
                });
            }

            setForm({ number: '', inboundAgentId: '', outboundAgentId: '' });
            setEditingId(null);
            setFormError('');
            fetchData();
        } catch (err) {
            console.error('Error saving phone number:', err);
        }
    };

    const handleEdit = (num) => {
        setForm({
            number: num.number,
            inboundAgentId: num.inbound_agent_id || '',
            outboundAgentId: num.outbound_agent_id || '',
        });
        setEditingId(num.id);
    };

    const handleDelete = (id) => {
        setSelectedDeleteId(id);
        setConfirmDeleteOpen(true);
    };

    const confirmDelete = async () => {
        if (!selectedDeleteId) return;
        try {
            await axios.delete(`/api/vapi/phone-numbers/${selectedDeleteId}`);
            fetchData();
        } catch (err) {
            console.error('Error deleting phone number:', err);
        } finally {
            setConfirmDeleteOpen(false);
            setSelectedDeleteId(null);
        }
    };

    const handleTwilioImport = async () => {
        if (!validatePhoneNumber(twilioForm.phoneNumber)) {
            alert('Invalid phone number. Use E.164 format, e.g., +1234567890');
            return;
        }

        try {
            await axios.post('/api/vapi/import-twilio', {
                phoneNumber: twilioForm.phoneNumber,
                accountSid: twilioForm.accountSid,
                authToken: twilioForm.authToken,
                label: twilioForm.label,
                inboundAgentId: twilioForm.inboundAgentId || null,
                outboundAgentId: twilioForm.outboundAgentId || null
            });

            setTwilioForm({
                phoneNumber: '',
                accountSid: '',
                authToken: '',
                label: '',
                inboundAgentId: '',
                outboundAgentId: ''
            });
            setTwilioModalOpen(false);
            fetchData();
        } catch (err) {
            console.error('Error importing from Twilio:', err);
        }
    };

    const handleGenericImport = async (provider) => {
        if (!validatePhoneNumber(providerForm.phoneNumber)) {
            alert('Invalid phone number. Use E.164 format, e.g., +1234567890');
            return;
        }

        try {
            await axios.post('/api/vapi/import-provider', {
                provider,
                phoneNumber: providerForm.phoneNumber,
                label: providerForm.label,
                credentials: providerForm.credentials,
                inboundAgentId: providerForm.inboundAgentId || null,
                outboundAgentId: providerForm.outboundAgentId || null
            });

            setProviderModalOpen(null);
            setProviderForm({
                phoneNumber: '',
                label: '',
                credentials: {},
                inboundAgentId: '',
                outboundAgentId: ''
            });
            fetchData();
        } catch (err) {
            console.error('Error importing from provider:', err);
        }
    };

    return (
        <Box p={3}>
            <Typography variant="h4" gutterBottom>📞 Phone Numbers</Typography>

            <Box display="flex" gap={2} mb={3} flexWrap="wrap">
                <TextField
                    label="Phone Number"
                    value={form.number}
                    onChange={(e) => setForm({ ...form, number: e.target.value })}
                    error={!!formError}
                    helperText={formError}
                />
                <TextField
                    label="Inbound Agent" select value={form.inboundAgentId}
                    onChange={(e) => setForm({ ...form, inboundAgentId: e.target.value })}
                    style={{ minWidth: 200 }}
                >
                    <MenuItem value="">None</MenuItem>
                    {agents.map(agent => (
                        <MenuItem key={agent.id} value={agent.id}>{agent.name || agent.id}</MenuItem>
                    ))}
                </TextField>
                <TextField
                    label="Outbound Agent" select value={form.outboundAgentId}
                    onChange={(e) => setForm({ ...form, outboundAgentId: e.target.value })}
                    style={{ minWidth: 200 }}
                >
                    <MenuItem value="">None</MenuItem>
                    {agents.map(agent => (
                        <MenuItem key={agent.id} value={agent.id}>{agent.name || agent.id}</MenuItem>
                    ))}
                </TextField>
                <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleAddOrUpdate}>
                    {editingId ? 'Update Number' : 'Add Number'}
                </Button>

                {/* Import buttons */}
                <Button variant="outlined" startIcon={<CloudUploadIcon />} onClick={() => setTwilioModalOpen(true)}>
                    Import from Twilio
                </Button>
                <Button variant="outlined" startIcon={<CloudUploadIcon />} onClick={() => setProviderModalOpen('vonage')}>
                    Import from Vonage
                </Button>
                <Button variant="outlined" startIcon={<CloudUploadIcon />} onClick={() => setProviderModalOpen('telnyx')}>
                    Import from Telnyx
                </Button>
                <Button variant="outlined" startIcon={<CloudUploadIcon />} onClick={() => setProviderModalOpen('sip')}>
                    Import BYO SIP Trunk
                </Button>
            </Box>

            <Paper>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Number</TableCell>
                            <TableCell>Inbound Agent</TableCell>
                            <TableCell>Outbound Agent</TableCell>
                            <TableCell>Imported At</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {numbers?.supabase?.map((num) => (
                            <TableRow key={num.id}>
                                <TableCell>{num.number}</TableCell>
                                <TableCell>{num.inbound_agent?.name || 'Unassigned'}</TableCell>
                                <TableCell>{num.outbound_agent?.name || 'Unassigned'}</TableCell>
                                <TableCell>{num.imported_at ? formatDateTime(num.imported_at) : 'N/A'}</TableCell>
                                <TableCell>
                                    <IconButton onClick={() => handleEdit(num)}><EditIcon /></IconButton>
                                    <IconButton onClick={() => handleDelete(num.id)}><DeleteIcon /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        {numbers?.vapi?.map((num) => (
                            <TableRow key={num.id}>
                                <TableCell>{num.number}</TableCell>
                                <TableCell colSpan={2}>Vapi only (not stored in Supabase)</TableCell>
                                <TableCell>{num.createdAt ? formatDateTime(num.createdAt) : 'N/A'}</TableCell>
                                <TableCell>
                                    <Button variant="outlined" onClick={() => {
                                        setForm({ number: num.number, inboundAgentId: '', outboundAgentId: '' });
                                        setEditingId(null);
                                    }}>
                                        Import
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>

            {/* Twilio Dialog */}
            <Dialog open={twilioModalOpen} onClose={() => setTwilioModalOpen(false)}>
                <DialogTitle>Import Phone Number from Twilio</DialogTitle>
                <DialogContent>
                    <TextField fullWidth margin="normal" label="Twilio Phone Number" value={twilioForm.phoneNumber}
                        onChange={(e) => setTwilioForm({ ...twilioForm, phoneNumber: e.target.value })} />
                    <TextField fullWidth margin="normal" label="Account SID" value={twilioForm.accountSid}
                        onChange={(e) => setTwilioForm({ ...twilioForm, accountSid: e.target.value })} />
                    <TextField fullWidth margin="normal" type="password" label="Auth Token" value={twilioForm.authToken}
                        onChange={(e) => setTwilioForm({ ...twilioForm, authToken: e.target.value })} />
                    <TextField fullWidth margin="normal" label="Label (optional)" value={twilioForm.label}
                        onChange={(e) => setTwilioForm({ ...twilioForm, label: e.target.value })} />
                    <TextField label="Inbound Agent" select fullWidth margin="normal" value={twilioForm.inboundAgentId}
                        onChange={(e) => setTwilioForm({ ...twilioForm, inboundAgentId: e.target.value })}>
                        <MenuItem value="">None</MenuItem>
                        {agents.map(agent => (
                            <MenuItem key={agent.id} value={agent.id}>{agent.name || agent.id}</MenuItem>
                        ))}
                    </TextField>
                    <TextField label="Outbound Agent" select fullWidth margin="normal" value={twilioForm.outboundAgentId}
                        onChange={(e) => setTwilioForm({ ...twilioForm, outboundAgentId: e.target.value })}>
                        <MenuItem value="">None</MenuItem>
                        {agents.map(agent => (
                            <MenuItem key={agent.id} value={agent.id}>{agent.name || agent.id}</MenuItem>
                        ))}
                    </TextField>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTwilioModalOpen(false)}>Cancel</Button>
                    <Button onClick={handleTwilioImport} variant="contained">Import</Button>
                </DialogActions>
            </Dialog>

            {/* Generic Provider Import Dialog */}
            <Dialog open={!!providerModalOpen} onClose={() => setProviderModalOpen(null)}>
                <DialogTitle>Import from {providerModalOpen?.toUpperCase()}</DialogTitle>
                <DialogContent>
                    <TextField fullWidth margin="normal" label="Phone Number"
                        value={providerForm.phoneNumber}
                        onChange={(e) => setProviderForm({ ...providerForm, phoneNumber: e.target.value })} />
                    <TextField fullWidth margin="normal" label="Label (optional)"
                        value={providerForm.label}
                        onChange={(e) => setProviderForm({ ...providerForm, label: e.target.value })} />
                    {providerModalOpen === 'vonage' && (
                        <>
                            <TextField fullWidth margin="normal" label="API Key"
                                onChange={(e) =>
                                    setProviderForm(prev => ({ ...prev, credentials: { ...prev.credentials, apiKey: e.target.value } }))
                                } />
                            <TextField fullWidth margin="normal" label="API Secret" type="password"
                                onChange={(e) =>
                                    setProviderForm(prev => ({ ...prev, credentials: { ...prev.credentials, apiSecret: e.target.value } }))
                                } />
                        </>
                    )}
                    {providerModalOpen === 'telnyx' && (
                        <TextField fullWidth margin="normal" label="API Key" type="password"
                            onChange={(e) =>
                                setProviderForm(prev => ({ ...prev, credentials: { ...prev.credentials, apiKey: e.target.value } }))
                            } />
                    )}
                    {providerModalOpen === 'sip' && (
                        <>
                            <TextField fullWidth margin="normal" label="SIP URI"
                                onChange={(e) =>
                                    setProviderForm(prev => ({ ...prev, credentials: { ...prev.credentials, sipUri: e.target.value } }))
                                } />
                            <TextField fullWidth margin="normal" label="Auth Password" type="password"
                                onChange={(e) =>
                                    setProviderForm(prev => ({ ...prev, credentials: { ...prev.credentials, authPassword: e.target.value } }))
                                } />
                        </>
                    )}
                    <TextField label="Inbound Agent" select fullWidth margin="normal"
                        value={providerForm.inboundAgentId}
                        onChange={(e) => setProviderForm({ ...providerForm, inboundAgentId: e.target.value })}>
                        <MenuItem value="">None</MenuItem>
                        {agents.map(agent => (
                            <MenuItem key={agent.id} value={agent.id}>{agent.name || agent.id}</MenuItem>
                        ))}
                    </TextField>
                    <TextField label="Outbound Agent" select fullWidth margin="normal"
                        value={providerForm.outboundAgentId}
                        onChange={(e) => setProviderForm({ ...providerForm, outboundAgentId: e.target.value })}>
                        <MenuItem value="">None</MenuItem>
                        {agents.map(agent => (
                            <MenuItem key={agent.id} value={agent.id}>{agent.name || agent.id}</MenuItem>
                        ))}
                    </TextField>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setProviderModalOpen(null)}>Cancel</Button>
                    <Button variant="contained" onClick={() => handleGenericImport(providerModalOpen)}>Import</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this phone number?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
                    <Button onClick={confirmDelete} color="error" variant="contained">Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PhoneNumbers;
